import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import InboxMock from '../components/InboxMock';
import FeeNotice from '../components/FeeNotice';

const Home: React.FC = () => {
  useEffect(() => { document.title = 'Haven Crest Health — Free patient advocacy and care coordination'; }, []);
  return (
    <main>
      <section className="hero">
        <div className="wrap grid grid--hero">
          <div className="hero__copy reveal">
            <h1 className="display">A patient advocate <em>in your corner.</em></h1>
            <p className="lede">
              Haven Crest Health is free patient advocacy and care coordination. A navigator gathers your
              medical records from every doctor and hospital, explains them in plain language, coordinates
              between your providers, and helps you find the right specialist when you need one.
            </p>
            <p className="lede" style={{ fontSize: '1.05rem' }}>
              It all runs through your own health inbox, where requesting records from any provider is a
              single click and we send the request on your behalf.
            </p>
            <div className="hero__ctas">
              <Link to="/contact" className="btn btn--accent">Get an advocate, free <span className="arrow">→</span></Link>
              <Link to="/services" className="btn btn--ghost">See how it works</Link>
            </div>
            <p className="hero__note">No charge from us, ever. Nothing is sent without your say-so. Leave whenever you like.</p>
          </div>
          <div className="reveal"><InboxMock /></div>
        </div>
      </section>

      <section className="section section--rule">
        <div className="wrap">
          <div className="eyebrow">What your advocate does</div>
          <h2 className="measure">The parts of healthcare nobody helps you with. Handled.</h2>
          <div className="grid grid--3" style={{ marginTop: '2.5rem' }}>
            <div className="card">
              <div className="card__ico card__ico--accent">↗</div>
              <h3>Gathers your records</h3>
              <p>Every office, every hospital, every lab. You click <strong>Request</strong> in your inbox, we prepare and send it on your behalf, chase the office when it stalls, and file what comes back.</p>
            </div>
            <div className="card">
              <div className="card__ico card__ico--moss">✎</div>
              <h3>Explains and organizes</h3>
              <p>A plain-language summary of what your records actually say, a timeline of your care, and the questions worth asking, written by a person, not a chatbot.</p>
            </div>
            <div className="card">
              <div className="card__ico">☺</div>
              <h3>Coordinates your care</h3>
              <p>Finds a specialist for a second opinion, gets your records to a new doctor before the visit, preps you for appointments, and keeps your providers on the same page.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--rule">
        <div className="wrap">
          <div className="eyebrow">How it works</div>
          <h2 className="measure">Four steps. The first one takes about a minute.</h2>
          <div className="steps" style={{ marginTop: '2.5rem' }}>
            <div className="step"><h3>Meet your navigator</h3><p>Sign up and you get a navigator and your own @havencresthealth.com inbox. Both free, for as long as you want them.</p></div>
            <div className="step"><h3>Tell us your doctors</h3><p>Add the offices, clinics, or hospitals you've been to. A name and a city is enough; we find the rest.</p></div>
            <div className="step"><h3>Click Request</h3><p>We send the records request on your behalf, chase the office if it goes quiet, and deliver the records straight to your inbox.</p></div>
            <div className="step"><h3>Decide together</h3><p>Your navigator summarizes what came back, lines up options, and helps you take the next step. Every decision stays yours.</p></div>
          </div>
        </div>
      </section>

      <section className="section section--rule">
        <div className="wrap grid grid--split">
          <div>
            <div className="eyebrow">Care navigation</div>
            <h2>A real person, <em>not a portal.</em></h2>
            <p className="lede">
              Your navigator is a person you can email or call. They know your file, they follow up with
              offices for you, and they help you decide what to do next. That's the service; the inbox is
              just where it lives.
            </p>
            <ul className="navlist">
              <li><strong>Plain-language summaries.</strong> What a report or chart actually says, and what's worth asking about.</li>
              <li><strong>Finding the right doctor.</strong> A specialist for a second opinion or a new primary doctor, with your records ready to share.</li>
              <li><strong>Appointment prep.</strong> The questions to bring, the records to bring, and what to expect.</li>
            </ul>
            <div className="hero__ctas">
              <Link to="/care-navigators" className="btn btn--ink">Meet the navigators <span className="arrow">→</span></Link>
            </div>
          </div>
          <div className="grid" style={{ gap: '1rem', alignSelf: 'center' }}>
            <div className="eyebrow" style={{ marginBottom: 0 }}>Where we go deepest</div>
            <Link to="/oncology" className="card card--link">
              <div className="card__ico card__ico--accent">🧫</div>
              <h3>Cancer care</h3>
              <p>Pathology, imaging, and infusion records from every system, in one folder, with help getting a second opinion.</p>
            </Link>
            <Link to="/cardiovascular" className="card card--link">
              <div className="card__ico">📈</div>
              <h3>Heart health</h3>
              <p>Echo, stress, and cath reports plus hospital records, together, and a hand finding the right cardiologist.</p>
            </Link>
            <Link to="/nutrition" className="card card--link">
              <div className="card__ico card__ico--moss">🧪</div>
              <h3>Nutrition</h3>
              <p>Labs over time, GI records, and dietitian notes, lined up so a new dietitian can start where the last left off.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <div className="band">
            <div className="band__grid">
              <div>
                <span className="stamp">Free and voluntary</span>
                <h2 style={{ marginTop: '1.2rem' }}>Here's exactly what that means.</h2>
                <p className="muted measure">We'd rather over-explain than surprise you.</p>
              </div>
              <div className="grid" style={{ gap: '1.2rem' }}>
                <div>
                  <h3>Free to use</h3>
                  <p className="muted">The inbox, the one-click requests, the follow-up calls, the tracker, and the navigation help cost you nothing. No subscription, no bill from us.</p>
                </div>
                <div>
                  <h3>Entirely voluntary</h3>
                  <p className="muted">You decide which records to request and when. Nothing is sent without your click. Close your account any time; your records stay yours.</p>
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '1.5rem' }}><FeeNotice /></div>
        </div>
      </section>

      <section className="section section--rule">
        <div className="wrap center">
          <h2>You shouldn't have to manage your own care alone.</h2>
          <p className="lede">A free advocate who gathers your records, explains them, and coordinates your care. Voluntary, and yours to keep.</p>
          <div className="hero__ctas" style={{ justifyContent: 'center' }}>
            <Link to="/contact" className="btn btn--ink">Get an advocate, free <span className="arrow">→</span></Link>
            <Link to="/about" className="btn btn--ghost">Why we built this</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
